/**
 * todo this logic should be moved to an effect, since it is long running
 * JIRA: OBGB-8969
 * */
import { Injectable, OnDestroy } from "@angular/core";
import { MatLegacySnackBarRef as MatSnackBarRef } from "@angular/material/legacy-snack-bar";
import { select, Store } from "@ngrx/store";
import { updateQueryStringParameters } from "@obg/global.evo";
import { GtmEventAction } from "@obg/gtm.types/adaptive";
import {
	ANNUAL_DATA_VALIDATION_ALERT_DIALOG_ID,
	AnnualDataValidationAlertContainer,
	BrandMigration,
	MigrationNotificationDialogService,
} from "@obg/mat.ui.common";
import {
	AlertButtonColor,
	AlertButtonStyle,
	DialogConfig,
	DialogService,
	SnackBarComponentBase,
} from "@obg/mat.ui.core";
import {
	CACHE_STORE_MISSING_PASSWORD,
	CommonResponsibleGamingSelector,
	CommonState,
	CustomerVerificationConfigService,
	CustomerVerificationService,
	getNetlossLimitConfirmation,
	KycConfig,
	MissingPasswordConfigService,
	SKIP_MISSING_PASSWORD_KEY,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	AuthenticationStatus,
	AuthSelector,
	CORE_ROUTE,
	CustomerComplianceService,
	CustomerManagementSelector,
	DialogStatusSelector,
	DisallowedActionCodes,
	Jurisdiction,
	JurisdictionSelector,
	KycState,
	LimitsModalType,
	LimitsReminderAction,
	MessageType,
	RESPONSIBLE_GAMING_MESSAGE_CONTEXT,
	ResponsibleGamingConfigService,
	AccountStatus,
	RequiredActions,
} from "@obg/ngx.core";
import { CoreContentState, ImageSelector, TranslationGroup } from "@obg/ngx.core.content";
import { CacheFactory, CacheStore, DeviceConfigService, FeatureFlagService, StorageMode } from "@obg/ngx.global";
import { PlatformService, TelemetryService } from "@obg/ngx.global.evo";
import { Route, RouteAction, RouteSelector, RouteService } from "@obg/ngx.router";
import { LotbaSelfExclusionAlertContainer } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { subMinutes, compareAsc } from "date-fns";
import { BehaviorSubject, combineLatest, EMPTY, merge, Observable, of, Subscription, timer } from "rxjs";
import { debounceTime, expand, filter, map, pairwise, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { LoginConfigService } from "../../sdk";
import { SgaSetPasswordContainer } from "../../sga-set-password/sga-set-password.container";
import { VerificationDialogContainer } from "../../verification-dialog/verification-dialog.container";
import { LoginUiAlertService } from "../login-ui/login-ui-alert.service";
import { LoginUiFlowService } from "../login-ui/login-ui-flow.service";
import { LoginUiConfigService } from "../login.config";
import { LOGIN_LIMIT_ALERT_ID, UI_LOGIN_ALERT_ID } from "../login.const";
import { MgaFlowSteps } from "../new-login-flow/login-flow.const";
import { NewFlowService } from "../new-login-flow/new-flow.service";
import { SgaLoginNoticeModalComponent } from "../sga/sga-login-notice-modal/sga-login-notice-modal.component";

import {
	ACCOUNT_LIMIT_WARNING_ID,
	ACCOUNT_WILL_BE_LIMITED_WARNING_TITLE,
	DocumentExpiredDaysLeft,
	EXPIRED_DOCUMENTS_ALERT_MESSAGE,
	EXPIRED_DOCUMENTS_ICON,
	EXPIRED_DOCUMENTS_VERIFY_BUTTON,
	EXPIRED_DOCUMENTS_WARNING_ID,
	EXPIRED_DOCUMENTS_WARNING_TITLE,
	REGISTRATIONSUCCESSROUTES,
} from "./first-login.const";

/**
 * If you see this file, and by some bad luck need to extend it with more dialog/page event logic,
 * consider introducing workflows which can be listened/worked with thru state.
 * E.g. `welcome` workflow (which in it's turn can consist of welcome dialog + `depotit-limit->deposit` workflow).
 */
@Injectable({
	providedIn: "root",
})
export class FirstLoginService implements OnDestroy {
	sgaLoginAlertClosed$: Observable<boolean>;

	private cache: CacheStore;
	private displayLoginMessage$$: Subscription;
	private displayDocExpiredNotification$$ = new Subscription();
	private sgaLoginAlertClosedSub$: BehaviorSubject<boolean>;

	constructor(
		private store: Store<CommonState & CoreContentState>,
		private responsibleGamingSelector: CommonResponsibleGamingSelector,
		private customerVerificationService: CustomerVerificationService,
		private loginUiFlowService: LoginUiFlowService,
		private loginUiAlertService: LoginUiAlertService,
		private dialogService: DialogService,
		private translatableAlertService: AbstractTranslatableAlertService,
		private platformService: PlatformService,
		private loginConfigService: LoginConfigService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private customerVerificationConfig: CustomerVerificationConfigService,
		private telemetryService: TelemetryService,
		private imageSelector: ImageSelector,
		private customerComplianceService: CustomerComplianceService,
		private missingPasswordConfigService: MissingPasswordConfigService,
		private deviceConfigService: DeviceConfigService,
		private loginUiConfigService: LoginUiConfigService,
		private newFlowService: NewFlowService,
		private dialogStatusSelector: DialogStatusSelector,
		private responsibleGamingConfigService: ResponsibleGamingConfigService,
		private routeService: RouteService,
		private kycConfig: KycConfig,
		private migrationNotificationDialogService: MigrationNotificationDialogService,
		private featureFlagService: FeatureFlagService,
		cacheFactory: CacheFactory
	) {
		this.displayLoginMessage$$ = this.displayLoginMessage$().subscribe();
		this.cache = cacheFactory.get(CACHE_STORE_MISSING_PASSWORD, {
			storageMode: StorageMode.LocalStorage,
		});

		this.sgaLoginAlertClosedSub$ = new BehaviorSubject(false);
		this.sgaLoginAlertClosed$ = this.sgaLoginAlertClosedSub$.asObservable();
	}

	initializeWelcomeFlow$(): Observable<void> {
		const welcomeFlow$ = of(undefined).pipe(
			tap(() => this.customerVerificationService.initialize()),
			switchMap(() => this.customerVerificationService.getAvailableRewards$()),
			take(1),
			withLatestFrom(
				this.store.select(RouteSelector.getReferer),
				this.store.pipe(RouteSelector.getName$),
				this.store.select(RouteSelector.getQueryParams)
			),
			switchMap(([registerRewardsPayload, referer, routeName, incomingQueryParams]) => {
				const isNewLoginFlowEnabled =
					this.loginUiConfigService.getNewLoginFlowEnabled() &&
					!!this.loginUiConfigService.getNewLoginFlowSteps().length;

				const queryParamsAction = isNewLoginFlowEnabled ? null : CORE_ROUTE.registration.registerSuccess;

				const queryParams = {
					variant: registerRewardsPayload.verificationType,
					action: queryParamsAction,
				};
				const isDesktop = this.deviceConfigService.isDesktop();
				const isExistsActionConfig = !!this.routeService.getRouteConfig(queryParamsAction ?? "");
				const shouldNavigateToReturnUrl = !isExistsActionConfig && !!incomingQueryParams?.returnUrl;

				if (isNewLoginFlowEnabled) {
					const hasOpenedFlowDialog = this.newFlowService.isLoginFlowDialogOpen();
					if (!hasOpenedFlowDialog) {
						this.newFlowService.openMgaDialog(MgaFlowSteps.Deposit);
					}
				}

				if (shouldNavigateToReturnUrl) {
					this.store.dispatch(
						RouteAction.navigateByUrl(incomingQueryParams!.returnUrl as string, { replaceUrl: true })
					);
					return of(undefined);
				}

				if (!REGISTRATIONSUCCESSROUTES.includes(routeName)) {
					this.store.dispatch(RouteAction.setQueryParams(queryParams));
					this.registrationSuccessMessage(isDesktop);
					return of(undefined);
				}

				const shouldNavigateToReferer = referer?.name && this.shouldNavigateToExternalUrl(referer);
				const hasNoValidReferer = !referer || !referer.data.actionType || !shouldNavigateToReferer;

				if (hasNoValidReferer) {
					const route = shouldNavigateToReferer ? referer!.name : CORE_ROUTE.home;
					this.store.dispatch(
						RouteAction.navigate(route, undefined, isExistsActionConfig ? { queryParams } : undefined)
					);
					this.registrationSuccessMessage(isDesktop);
					return of(undefined);
				}

				return this.customerComplianceService.kycStatusCheck$(referer.data.actionType).pipe(
					take(1),
					tap((action) => {
						this.registrationSuccessMessage(isDesktop);
						if (action.isAllowed) {
							this.store.dispatch(
								RouteAction.navigateByUrl(
									updateQueryStringParameters(referer!.externalUrl, queryParams)
								)
							);
							return;
						}

						this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home, undefined, { queryParams }));
					})
				);
			})
		);

		const loginStatusChanged$ = this.store.select(AuthSelector.getAuthenticationStatus).pipe(
			pairwise(),
			filter(
				([prev, next]) =>
					prev === AuthenticationStatus.NotAuthenticated && next === AuthenticationStatus.Authenticated
			)
		);

		return loginStatusChanged$.pipe(
			switchMap(() =>
				this.store.select(CustomerManagementSelector.getCustomer).pipe(
					ignoreNil(),
					take(1),
					withLatestFrom(this.customerComplianceService.isDutchKycRequested$()),
					map(([, isDutchKycRequested]) => {
						if (isDutchKycRequested) {
							this.store.dispatch(RouteAction.navigate(CORE_ROUTE.verification.root));
							return false;
						}
						return true;
					}),
					filter((shouldContinueWelcomeFlow) => shouldContinueWelcomeFlow),
					switchMap(() => this.shouldShowWelcomeFlow$()),
					switchMap((shouldShowWelcomeFlow) =>
						shouldShowWelcomeFlow ? welcomeFlow$ : this.showLoginAlert$()
					),
					switchMap(() => this.showFirstLoginAlert$()),
					map(() => undefined)
				)
			)
		);
	}

	ngOnDestroy() {
		this.displayDocExpiredNotification$$.unsubscribe();
	}

	displayLoginMessage$() {
		return this.store.select(RouteSelector.getQueryParams).pipe(
			map((queryParams: any) => queryParams?.displayLoginMessage),
			filter((shouldDisplay) => !!shouldDisplay),
			take(1),
			switchMap(() => this.showLoginAlert$()),
			tap(() => this.displayLoginMessage$$.unsubscribe())
		);
	}

	showLoginAlert$(): Observable<void> {
		if (this.platformService.isServer) {
			return EMPTY;
		}

		return this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			switchMap((jurisdiction) => {
				switch (jurisdiction) {
					case Jurisdiction.Dga:
						return this.dgaLoginAlert$();
					case Jurisdiction.Dgoj:
						return this.dgojLoginAlert$();
					case Jurisdiction.Hgc:
						return this.hgcLoginAlert$();
					case Jurisdiction.Mta:
						return this.mtaLoginAlert$();
					case Jurisdiction.Ksa:
						return this.ksaLoginAlert$();
					case Jurisdiction.Pega:
						return this.pegaLoginAlert$();
					case Jurisdiction.Aams:
						return this.aamsLoginAlert$();
					case Jurisdiction.COGA:
						return this.canShowLoginDateAndTimeAlert$(5000);
					case Jurisdiction.Cgl:
						return this.brandMigrationDialog$();
					case Jurisdiction.Tgc:
						return this.brandMigrationDialog$();
					case Jurisdiction.BRA:
						return merge(
							this.canShowLoginDateAndTimeAlert$(),
							this.canShowLoginAlert$().pipe(switchMap(() => this.handleAnnualValidation$()))
						);
					case Jurisdiction.Iplyc:
					case Jurisdiction.LPCSE:
						return this.loginDateAndTimeAlert$();
					case Jurisdiction.Lotba:
						return merge(this.loginDateAndTimeAlert$(), this.loginLimitAlert$());
					default:
						const brandMigration = this.featureFlagService.get<BrandMigration | undefined>(
							"mga_to_tobique_jurisdiction"
						)?.tobiqueMigration;

						return brandMigration?.tobiquePreMigrationIsEnabled
							? this.brandMigrationDialog$()
							: this.sgaLoginAlert$();
				}
			})
		);
	}

	showFirstLoginAlert$(): Observable<void> {
		if (this.platformService.isServer) {
			return EMPTY;
		}

		return this.store.select(AuthSelector.isFirstLogin).pipe(
			filter((isFirstLogin) => !!isFirstLogin),
			switchMap(() => this.store.select(JurisdictionSelector.get)),
			ignoreNil(),
			switchMap((jurisdiction) => {
				switch (jurisdiction) {
					case Jurisdiction.BGC:
						return this.bgcFirstLoginAlert$();
					default:
						return EMPTY;
				}
			})
		);
	}

	loginAlert$(afterDialogsClosed?: boolean): Observable<void> {
		return this.store.pipe(
			this.responsibleGamingSelector.postLoginMessage$({
				context: RESPONSIBLE_GAMING_MESSAGE_CONTEXT.loginAlert,
			}),
			switchMap(() => this.canShowLoginAlert$(afterDialogsClosed))
		);
	}

	pegaLoginAlert$(): Observable<void> {
		const isDesktop = this.deviceConfigService.isDesktop();
		const verificationRoute = CORE_ROUTE.verification.root;
		return this.canShowLoginAlert$().pipe(
			switchMap(() =>
				combineLatest([
					this.store.select(CustomerManagementSelector.getCustomer).pipe(ignoreNil()),
					this.store.select(RouteSelector.getCurrent),
				])
			),
			take(1),
			filter(([_, currentRoute]) => currentRoute?.name !== verificationRoute),
			withLatestFrom(this.customerComplianceService.getCurrentCompliance$()),
			tap(([, currentCompliance]) => {
				if (currentCompliance.kycState !== KycState.Accepted) {
					if (isDesktop) {
						this.dialogService
							.openComponent(VerificationDialogContainer, {
								disableClose: true,
								closeOnNavigation: true,
							})
							.afterClosed();
					} else {
						this.translatableAlertService.open({ customAlertComponent: VerificationDialogContainer }, []);
					}
				}
			}),
			map(() => undefined)
		);
	}

	ksaLoginAlert$(): Observable<void> {
		return this.canShowLoginAlert$().pipe(
			tap(() => {
				this.store.dispatch(LimitsReminderAction.showLimitsReminder({ payload: LimitsModalType.login }));
			})
		);
	}

	dgojLoginAlert$(): Observable<void> {
		return this.loginAlert$().pipe(
			switchMap(() => this.loginUiAlertService.dgojLoginAlertMessage$()),
			tap((message) => {
				this.translatableAlertService.open(
					{
						id: UI_LOGIN_ALERT_ID,
						title: "dgoj-login-alert.title",
						message,
						hideClose: true,
						buttons: [
							{
								text: "general.ok",
							},
						],
					},
					[TranslationGroup.General]
				);
			}),
			map(() => undefined)
		);
	}

	loginLimitAlert$(): Observable<void> {
		return this.loginAlert$().pipe(
			switchMap(() => this.responsibleGamingConfigService.getIsLoginLimitAlertEnabled$()),
			filter((isLoginLimitAlertEnabled) => Boolean(isLoginLimitAlertEnabled)),
			switchMap(() => this.store.select(CustomerManagementSelector.getCustomer)),
			ignoreNil(),
			take(1),
			filter((customer) => {
				const limit = this.responsibleGamingConfigService.getLoginLimitAlertLimit();
				const periodMinutes = this.responsibleGamingConfigService.getLoginLimitAlertPeriodMinutes();
				const lastLoginTime = customer.lastLogins[0] ? new Date(customer.lastLogins[0].timestamp) : new Date();
				const minTime = subMinutes(lastLoginTime, periodMinutes);
				const loginCount = customer.lastLogins.filter((login) => new Date(login.timestamp) > minTime).length;
				return loginCount >= limit;
			}),
			switchMap(() =>
				this.translatableAlertService.open$(
					{
						id: LOGIN_LIMIT_ALERT_ID,
						mustBeInteracted: true,
						hideClose: true,
						messageComponent: LotbaSelfExclusionAlertContainer,
						messageComponentInit: (c: LotbaSelfExclusionAlertContainer) =>
							(c.data = {
								title: "login-alert.login-limit.title",
								footerRedirectButton: "login-alert.login-limit.redirect-button-text",
							}),
						dialogConfig: {
							panelClass: "lotba-self-exclusion-wrapper",
						},
					},
					[]
				)
			),
			map(() => undefined)
		);
	}

	hgcLoginAlert$(): Observable<void> {
		return this.loginAlert$().pipe(
			take(1),
			switchMap(() => this.loginUiAlertService.getLoginDateAndTime$()),
			withLatestFrom(this.store.select(this.imageSelector.getByKey("general", "general.info-icon"))),
			switchMap(([params, icon]) =>
				this.snackBarService.openSnackBar$(
					{
						message: "hgc.login-alert.login-message",
						dismissible: true,
						duration: 10000,
						icon,
						type: MessageType.Info,
					},
					[TranslationGroup.Common],
					params
				)
			),
			tap(() => {
				this.handleDocumentExpiration();
			}),
			map(() => undefined)
		);
	}

	bgcFirstLoginAlert$(): Observable<void> {
		return this.loginAlert$(true).pipe(
			take(1),
			switchMap(() =>
				this.snackBarService.openSnackBar$(
					{
						message: "login-alert.deposit-alert-message",
						dismissible: true,
						duration: 30000,
						type: MessageType.Info,
						buttons: [
							{
								text: "login-alert.deposit-alert-button-text",
								handler: () =>
									this.store.dispatch(
										RouteAction.navigate(CORE_ROUTE.responsibleGaming.depositLimit)
									),
							},
						],
					},
					[TranslationGroup.Common]
				)
			),
			map(() => undefined)
		);
	}

	mtaLoginAlert$(): Observable<void> {
		return of(this.store.dispatch(getNetlossLimitConfirmation()));
	}

	sgaLoginAlert$(): Observable<void> {
		if (!this.loginConfigService.isSgaEnabled()) {
			return EMPTY;
		}

		const missingPasswordDialog$ = this.missingPasswordConfigService.getMissingPasswordEnabled()
			? combineLatest([
					this.cache.getItemAsObservable(SKIP_MISSING_PASSWORD_KEY),
					this.store.select(CustomerManagementSelector.isPasswordMissing),
				]).pipe(
					take(1),
					switchMap(([skipPasswordFromCache, isPasswordMissing]) =>
						!skipPasswordFromCache && isPasswordMissing
							? this.dialogService
									.openComponent(SgaSetPasswordContainer, { disableClose: true })
									.afterClosed()
							: of(true)
					)
				)
			: of(undefined);

		return this.store.pipe(
			this.responsibleGamingSelector.postLoginMessage$({
				context: RESPONSIBLE_GAMING_MESSAGE_CONTEXT.loginAlert,
			}),
			switchMap(() => missingPasswordDialog$),
			switchMap(() => this.canShowLoginAlert$(true)),
			switchMap(() => this.loginUiFlowService.sgaLoginAlert$()),
			tap((sgaLoginMessages) => {
				const dialogConfig: Partial<DialogConfig> = {
					panelClass: "obg-m-alert-extra-wide",
					disableClose: true,
				};

				if (!this.deviceConfigService.isDesktop()) {
					dialogConfig.width = "100%";
					dialogConfig.height = "100%";
					dialogConfig.maxWidth = "none";
					dialogConfig.maxHeight = "none";
				}

				this.translatableAlertService.open(
					{
						id: UI_LOGIN_ALERT_ID,
						title: "sga-login-alert.title",
						messageComponent: SgaLoginNoticeModalComponent,
						messageComponentInit: (component: SgaLoginNoticeModalComponent) =>
							(component.data = sgaLoginMessages),
						buttons: [
							{
								text: "general.close",
								style: AlertButtonStyle.Flat,
								color: AlertButtonColor.Accent,
								handler: () => {
									this.sgaLoginAlertClosedSub$.next(true);
								},
							},
						],
						dialogConfig,
						hideClose: false,
					},
					[TranslationGroup.General]
				);
			}),
			map(() => undefined)
		);
	}

	dgaLoginAlert$(): Observable<void> {
		return this.loginAlert$().pipe(
			take(1),
			switchMap(() => this.loginUiFlowService.dgaLoginAlert$()),
			withLatestFrom(this.store.select(this.imageSelector.getByKey("general", "general.info-icon"))),
			switchMap(([dgaLoginMessages, icon]) =>
				this.snackBarService.openSnackBar$(
					{
						message: "dga.login-alert.login-message",
						dismissible: true,
						duration: 10000,
						icon,
						type: MessageType.Info,
					},
					[TranslationGroup.Common],
					dgaLoginMessages
				)
			),
			map(() => undefined)
		);
	}

	aamsLoginAlert$(): Observable<void> {
		return this.loginDateAndTimeSnackBar$().pipe(
			switchMap((snackBarRef) =>
				snackBarRef.afterDismissed().pipe(
					take(1),
					switchMap(() => this.store.select(CustomerManagementSelector.getAllowedAction)),
					map((allowedAction) => allowedAction?.bet),
					filter(
						(bet) =>
							!bet?.isAllowed &&
							!!bet?.disallowedReasons.includes(DisallowedActionCodes.M_CURRENTCUSTOMER_SELFEXCLUDED)
					),
					withLatestFrom(this.store.select(this.imageSelector.getByKey("general", "general.clock-icon"))),
					switchMap(([, icon]) =>
						this.snackBarService.openSnackBar$({
							message: "responsible-gaming.selfexcluded",
							action: "general.close",
							type: MessageType.Warn,
							keepOpen: true,
							icon,
						})
					)
				)
			),
			map(() => undefined)
		);
	}

	canShowLoginDateAndTimeAlert$(duration = 10000): Observable<void> {
		return this.canShowLoginAlert$().pipe(
			take(1),
			switchMap(() => this.loginUiAlertService.getLoginDateAndTime$()),
			withLatestFrom(this.store.select(this.imageSelector.getByKey("general", "general.info-icon"))),
			switchMap(([params, icon]) =>
				this.snackBarService.openSnackBar$(
					{
						message: "login-alert.login-message",
						dismissible: true,
						duration,
						icon,
						type: MessageType.Info,
					},
					[TranslationGroup.Common],
					params
				)
			),
			map(() => undefined)
		);
	}

	brandMigrationDialog$(): Observable<void> {
		return this.canShowLoginAlert$().pipe(
			take(1),
			switchMap(() =>
				this.loginUiFlowService.detectMigrationNotification$().pipe(
					filter((shouldShowNotification) => shouldShowNotification),
					tap(() => this.migrationNotificationDialogService.open()),
					map(() => undefined)
				)
			)
		);
	}

	loginDateAndTimeSnackBar$(): Observable<MatSnackBarRef<SnackBarComponentBase>> {
		return this.loginAlert$().pipe(
			take(1),
			switchMap(() => this.loginUiAlertService.getLoginDateAndTime$()),
			withLatestFrom(this.store.select(this.imageSelector.getByKey("general", "general.info-icon"))),
			switchMap(([params, icon]) =>
				this.snackBarService.openSnackBar$(
					{
						message: "login-alert.login-message",
						dismissible: true,
						duration: 10000,
						icon,
						type: MessageType.Info,
					},
					[TranslationGroup.Common],
					params
				)
			)
		);
	}

	loginDateAndTimeAlert$(): Observable<void> {
		return this.loginDateAndTimeSnackBar$().pipe(map(() => undefined));
	}

	shouldShowWelcomeFlow$(): Observable<boolean> {
		return this.store.select(CustomerManagementSelector.getCustomer).pipe(
			ignoreNil(),
			take(1),
			withLatestFrom(
				this.customerComplianceService.getCurrentCompliance$(),
				this.store.select(AuthSelector.isFirstLogin)
			),
			map(([customer, compliance, isFirstLogin]) => {
				if (compliance.type !== "Dutch") {
					return !!isFirstLogin;
				}

				if (!customer.kyc.lastChangeTimestamp) {
					return compliance.kycState === KycState.Accepted && !!isFirstLogin;
				}

				const lastLogins = customer.lastLogins;

				const kycChangedTimestamp = new Date(customer.kyc.lastChangeTimestamp);
				const lastLogin = new Date(lastLogins[0].timestamp);

				if (lastLogins.length === 1) {
					return kycChangedTimestamp <= lastLogin;
				}

				const previousLogin = new Date(lastLogins[1].timestamp);

				return kycChangedTimestamp <= lastLogin && kycChangedTimestamp >= previousLogin;
			})
		);
	}

	canShowLoginAlert$(afterDialogsClosed?: boolean): Observable<void> {
		const notAllowedRoutes = [
			CORE_ROUTE.reward.depositBonus,
			CORE_ROUTE.payment.deposit,
			CORE_ROUTE.responsibleGaming.depositLimit,
			CORE_ROUTE.reward.welcomeBonus,
			CORE_ROUTE.commonPokerLogin,
		];
		const isRouteAllowed = (route: Route | undefined) => !!route && !notAllowedRoutes.includes(route.name);
		const isRouteNotAllowed$ = this.store
			.select(RouteSelector.getCurrent)
			.pipe(map((currentRoute) => !isRouteAllowed(currentRoute)));
		const isNavigating$ = this.store.select(RouteSelector.isNavigating).pipe(debounceTime(100));
		const hasOpenDialogs$ = this.store.select(this.dialogStatusSelector.hasOpenDialogs());
		return this.retryIfAnyConditionIsMet$([
			isRouteNotAllowed$,
			isNavigating$,
			...(afterDialogsClosed ? [hasOpenDialogs$] : []),
		]);
	}

	private retryIfAnyConditionIsMet$(conditions: Observable<boolean>[]): Observable<void> {
		const conditionsCheck$ = combineLatest(conditions).pipe(
			map((results) => results.reduce((acc, condition) => acc && !condition, true)),
			map((conditionsAreFalse, index) => [conditionsAreFalse, index] as [boolean, number]),
			filter(([conditionsAreFalse]) => conditionsAreFalse),
			take(1),
			map(([, attempt]) => attempt)
		);

		const delayedConditionsCheck$ = timer(500).pipe(switchMap(() => conditionsCheck$));

		return conditionsCheck$.pipe(
			expand((attempt) => (attempt === 0 ? EMPTY : delayedConditionsCheck$)),
			filter((attempt) => attempt === 0),
			map(() => undefined)
		);
	}

	private shouldNavigateToExternalUrl(referer: Route | undefined): boolean {
		const excludedRouteNames = this.loginConfigService.getExcludedRefererRouteNames();
		return !!(referer?.externalUrl && !excludedRouteNames.includes(referer.name));
	}

	private registrationSuccessMessage(isDesktop: boolean) {
		if (!this.customerVerificationConfig.isNoWelcomePageAfterLoginEnabled() || !isDesktop) {
			return;
		}

		this.snackBarService
			.openSnackBar$(
				{
					message: "customer-verification.account-created-short",
					action: "general.close",
					duration: 5000,
					type: MessageType.Success,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
	}

	private handleDocumentExpiration() {
		this.displayDocExpiredNotification$$ = this.customerComplianceService
			.getCurrentCompliance$()
			.pipe(take(1))
			.subscribe(({ accountInformation }) => {
				if (!accountInformation) {
					return;
				}
				const isAccountTemporaryExpired = accountInformation?.accountStatus === AccountStatus.TemporaryExpired;
				const isDocumentExpired = this.kycConfig.getDocumentExpiredEnabled() && isAccountTemporaryExpired;

				if (isDocumentExpired) {
					this.handleDocumentExpirationAlert(accountInformation.accountExpiry);
				}
			});
	}

	private calculateAccountExpiredDocumentDays(dateString?: string): number {
		if (!dateString) {
			return 0;
		}

		const isValidYearFormat = dateString.slice(0, 10).split("-")[2].length === 4;
		if (isValidYearFormat) {
			dateString = `${dateString.slice(0, 10).split("-").reverse().join("-")}T${dateString.split("T")[1]}`;
		}

		const millisecondsPerSecond = 1000;
		const secondsPerHour = 3600;
		const hoursPerDay = 24;
		const millisecondsPerDay = millisecondsPerSecond * secondsPerHour * hoursPerDay;
		const date = new Date(dateString);
		const difference = date.getTime() - new Date().getTime();
		const differenceInDays = Math.ceil(difference / millisecondsPerDay);

		return differenceInDays;
	}

	handleDocumentExpirationAlert(daysLeft: string) {
		const docExpiredDaysLeft = this.calculateAccountExpiredDocumentDays(daysLeft);
		const documentTitle =
			docExpiredDaysLeft <= DocumentExpiredDaysLeft.DOCUMENTS_EXPIRED
				? ACCOUNT_WILL_BE_LIMITED_WARNING_TITLE
				: EXPIRED_DOCUMENTS_WARNING_TITLE;
		const documentId =
			docExpiredDaysLeft <= DocumentExpiredDaysLeft.DOCUMENTS_EXPIRED
				? EXPIRED_DOCUMENTS_WARNING_ID
				: ACCOUNT_LIMIT_WARNING_ID;

		this.openDocumentExpirationAlert(documentTitle, documentId, docExpiredDaysLeft);
	}

	openDocumentExpirationAlert(title: string, id: string, daysLeft: number) {
		const verifyButton = "Verify my Document";
		const closeButton = "Close";
		const buttons = [
			{
				text: EXPIRED_DOCUMENTS_VERIFY_BUTTON,
				handler: () => {
					this.trackEventExpiredDocuments(true, daysLeft, verifyButton);
					this.store.dispatch(RouteAction.navigate(CORE_ROUTE.verification.kycMethodSelection));
				},
				style: AlertButtonStyle.Flat,
				color: AlertButtonColor.Accent,
			},
			{
				text: "general.close",
				handler: () => {
					this.trackEventExpiredDocuments(true, daysLeft, closeButton);
				},
				style: AlertButtonStyle.Transparent,
				color: AlertButtonColor.Primary,
			},
		];

		const alertConfig = {
			id: id,
			testId: id,
			title: title,
			brandLogo: {
				imageContent: EXPIRED_DOCUMENTS_ICON,
				imageContentGroup: "common",
			},
			message: EXPIRED_DOCUMENTS_ALERT_MESSAGE,
			buttons: buttons,
			dialogConfig: {
				panelClass: "north-star-alert-wrapper-horizontal-buttons",
			},
			verticalButtons: true,
		};
		this.translatableAlertService.open(alertConfig, [], { avaliableDaysLeft: daysLeft });
		this.trackEventExpiredDocuments(false, daysLeft);
	}

	private trackEventExpiredDocuments(isElementClicked: boolean, daysLeft: number, elementName?: string): void {
		const gtmElementName = "KYC Document Banner";
		const isExpired = daysLeft <= DocumentExpiredDaysLeft.DOCUMENTS_EXPIRED;
		const popupType = isExpired ? "2Popup" : "1Popup";
		const action = isElementClicked ? GtmEventAction.ElementClicked : GtmEventAction.ElementSeen;
		const elementId = isElementClicked ? `${elementName}-${daysLeft}-${popupType}` : `${daysLeft}-${popupType}`;

		this.telemetryService.track({
			event: action,
			ElementID: elementId,
			ElementName: gtmElementName,
		});
	}

	/**
	 * Checks if an annual profile validation reminder needs to be shown to the user.
	 * If the current date is greater or equal to the `activeFrom` date, it will display an alert to the user.
	 * Used for Betsson Brazil.
	 *
	 * @returns Observable<void> - Emits a value to trigger the alert, completes after showing it once per session.
	 */
	handleAnnualValidation$(): Observable<void> {
		return this.store.select(CustomerManagementSelector.getRequiredActions).pipe(
			take(1),
			filter((requiredActions) => !!requiredActions && requiredActions.length > 0),
			map((requiredActions) => {
				const accountDataValidationAction = requiredActions?.find(
					(action) => action.actionType === RequiredActions.AccountDataValidation
				);
				return accountDataValidationAction;
			}),
			filter((action) => !!action),
			map((action) => {
				if (action) {
					const activeFromDate = new Date(action.activeFrom);
					const currentDate = new Date();
					const activeDateReached = compareAsc(currentDate, activeFromDate) >= 0;

					if (activeDateReached) {
						this.dialogService.openComponent(AnnualDataValidationAlertContainer, {
							id: ANNUAL_DATA_VALIDATION_ALERT_DIALOG_ID,
							disableClose: true,
							closeOnNavigation: false,
						});
					}
				}
				return undefined;
			})
		);
	}
}
